import PropTypes from 'prop-types';
import styles from './FJPhotoRemoveBgThumbnailItem.module.less';
import { useRef } from 'react';
import { useEffect } from 'react';
import { useCallback } from 'react';
import FJPhotoRemoveBgCanvas, {
    BACKGROUND_TYPE,
} from '../../../FJMainPreview/FJCreationEditPanel/js/FJPhotoRemoveBgCanvas';
import { useState } from 'react';
import FJConstant from '../../../../constants/FJConstant';
import FJLoading from '../../../../common/components/FJLoading/FJLoading';
import FJWarnIcon from '../../../../assets/svg/warn.svg?react';
import FJPhotoBgRemoverStoreSingleton from '../../../../store/FJPhotoBgRemoverStore';
import _ from 'lodash';
import FJUtil from '../../../../util/FJUtil';
import FJCanvasUtil from '../../../../util/FJCanvasUtil';
import { useMemo } from 'react';

const FJPhotoRemoveBgThumbnailItem = ({ autoBgImageData, handleAddThumbnail, handleUpdateThumbnail }) => {
    const { originImgUrl, taskStatus, bgData, strokeData, maskKey, id, name } = autoBgImageData;

    const [loaded, setLoaded] = useState(false);
    const [transparentBgRect, setTransparentBgRect] = useState(null);
    const [transparentBgIsShow, setTransparentBgIsShow] = useState(true);

    const thumbnailCanvasRef = useRef(null);

    const viewCanvasRef = useRef(null);
    const transparentCanvasRef = useRef(null);

    const debounceCreateThumbnailUrl = useRef(null);

    const drawThumbnail = useCallback(
        (must = false) => {
            if (!loaded && !must) return;

            const ctx = viewCanvasRef.current.getContext('2d');

            const size = thumbnailCanvasRef.current.getSize();

            const maxDimension = 80;
            const aspectRatio = size.width / size.height;

            const rect = {
                x: 0,
                y: 0,
                width: size.width,
                height: size.height,
            };

            if (aspectRatio > 1) {
                rect.width = maxDimension;
                rect.height = maxDimension / aspectRatio;
            } else {
                rect.height = maxDimension;
                rect.width = maxDimension * aspectRatio;
            }

            viewCanvasRef.current.width = rect.width;
            viewCanvasRef.current.height = rect.height;

            thumbnailCanvasRef.current.draw(ctx, rect, {
                isShowPhotoRemoveBg: taskStatus === FJConstant.DEFAULT,
                isNotDrawTransparentBG: true,
            });

            if (taskStatus === FJConstant.DEFAULT) {
                if (!debounceCreateThumbnailUrl.current) {
                    debounceCreateThumbnailUrl.current = _.debounce(
                        () => {
                            const thumbnailUrl = viewCanvasRef.current.toDataURL();
                            handleUpdateThumbnail({ id, status: FJConstant.SUCCESS, thumbnailUrl });
                        },
                        200,
                        { trailing: true },
                    );
                }
                debounceCreateThumbnailUrl.current();
            }
        },
        [loaded, taskStatus, handleUpdateThumbnail, id],
    );

    useEffect(() => {
        const ctx = transparentCanvasRef.current.getContext('2d');

        transparentCanvasRef.current.width = 80;
        transparentCanvasRef.current.height = 80;

        FJCanvasUtil.drawTransparentBackground(ctx, 80, 80, 8);
    }, []);

    useEffect(() => {
        const init = async () => {
            if (!thumbnailCanvasRef.current) {
                thumbnailCanvasRef.current = new FJPhotoRemoveBgCanvas();
                thumbnailCanvasRef.current.setTransparentSize(108);
            }

            await thumbnailCanvasRef.current.init(originImgUrl);
            drawThumbnail(true);
            setLoaded(true);

            const size = thumbnailCanvasRef.current.getSize();
            const aspectRatio = size.width / size.height;
            const transparentBgSize = {
                width: aspectRatio > 1 ? 80 : 80 * aspectRatio,
                height: aspectRatio > 1 ? 80 / aspectRatio : 80,
            };
            setTransparentBgRect({
                x: (80 - transparentBgSize.width) / 2,
                y: (80 - transparentBgSize.height) / 2,
                width: transparentBgSize.width,
                height: transparentBgSize.height,
            });
        };

        setLoaded(false);
        originImgUrl !== '' &&
            handleAddThumbnail({ id, status: FJConstant.LOADING, thumbnailUrl: '', name, isSelected: true });
        originImgUrl !== '' && init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [originImgUrl, handleAddThumbnail, name]);

    useEffect(() => {
        if (!loaded || taskStatus !== FJConstant.DEFAULT || bgData === null) return;

        const bgInfo = thumbnailCanvasRef.current.getBackgroundInfo();
        if (FJUtil.deepEqual(bgInfo, bgData)) return;

        if (bgData.backgroundType === BACKGROUND_TYPE.TRANSPARENT) {
            setTransparentBgIsShow(true);
        } else {
            setTransparentBgIsShow(false);
        }

        const setBackgroundInfo = async () => {
            await thumbnailCanvasRef.current.setBackgroundInfo(bgData);
            drawThumbnail();
        };

        setBackgroundInfo();
    }, [bgData, drawThumbnail, loaded, taskStatus]);

    useEffect(() => {
        if (!loaded || taskStatus !== FJConstant.DEFAULT) return;

        const setMaskData = async () => {
            let maskData = null;
            if (maskKey !== null) {
                maskData = await FJPhotoBgRemoverStoreSingleton.getAutoLocalForageItem(maskKey);
            }

            thumbnailCanvasRef.current.setMaskData(maskData);
            thumbnailCanvasRef.current.updateView();
            drawThumbnail();
        };

        setMaskData();
    }, [maskKey, loaded, taskStatus, drawThumbnail]);

    useEffect(() => {
        if (!loaded || taskStatus !== FJConstant.DEFAULT || strokeData === null) return;

        const strokeInfo = thumbnailCanvasRef.current.getStrokeInfo();

        if (FJUtil.deepEqual(strokeInfo, strokeData)) return;
        thumbnailCanvasRef.current.setStrokeInfo(strokeData);
        drawThumbnail();
    }, [strokeData, loaded, taskStatus, drawThumbnail]);

    const transparentBgStyle = useMemo(() => {
        if (!transparentBgRect || !transparentBgIsShow) return { opacity: 0 };

        return {
            clipPath: `inset(${transparentBgRect.y}px ${transparentBgRect.x}px ${transparentBgRect.y}px ${transparentBgRect.x}px)`,
        };
    }, [transparentBgRect, transparentBgIsShow]);

    return (
        <div className={styles.item}>
            <canvas
                ref={transparentCanvasRef}
                className={styles.canvas_transparent}
                style={transparentBgStyle}
            ></canvas>

            <canvas ref={viewCanvasRef} className={styles.canvas_view}></canvas>

            {taskStatus === FJConstant.START && (
                <div className={styles.mask}>
                    <FJLoading className={styles.loading} />
                </div>
            )}

            {taskStatus === FJConstant.ERROR && (
                <div className={styles.mask}>
                    <FJWarnIcon className={styles.warn_icon} />
                </div>
            )}
        </div>
    );
};

FJPhotoRemoveBgThumbnailItem.propTypes = {
    autoBgImageData: PropTypes.object.isRequired,
    handleAddThumbnail: PropTypes.func.isRequired,
    handleUpdateThumbnail: PropTypes.func.isRequired,
};

export default FJPhotoRemoveBgThumbnailItem;
